import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { toast } from "react-toastify"

import {
  useGetSmartHomeAlexaDevicesQuery,
  useGetSmartHomeDevicesQuery,
  useLogoutMobileUserMutation,
  useRequestOAuthCodeMutation,
  useSetSmartHomeAlexaEnabledMutation,
} from "store/api/mobileApi"
import logo from "assets/svg/logo-white.svg"

import { clearMobileAuthToken, getMobileAuthToken } from "utils/mobileAuthToken"
import { buildOauthLoginParams } from "utils/oauthLoginParams"

import styles from "./index.module.scss"

const linkingErrorMessage = "We couldn't finish linking your account. Please try again."
const linkingSelectionMessage = "Select at least one device to continue."
type OAuthLoginParams = NonNullable<ReturnType<typeof buildOauthLoginParams>>

export const SmartHomeDevices = () => {
  const location = useLocation()
  const token = getMobileAuthToken()
  const oauthLoginParams = buildOauthLoginParams(new URLSearchParams(location.search))
  const {
    data: devicesData,
    isLoading: isLoadingDevices,
    isError: isDevicesError,
    error: devicesError,
  } = useGetSmartHomeDevicesQuery(undefined, { skip: !token })
  const {
    data: enabledDevicesData,
    isLoading: isLoadingEnabledDevices,
    isError: isEnabledDevicesError,
    error: enabledDevicesError,
  } = useGetSmartHomeAlexaDevicesQuery(undefined, { skip: !token })
  const [requestOAuthCode] = useRequestOAuthCodeMutation()
  const [setEnabled, { isLoading: isSaving }] = useSetSmartHomeAlexaEnabledMutation()
  const [logoutMobileUser] = useLogoutMobileUserMutation()
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<string[]>([])
  const [statusMessage, setStatusMessage] = useState("")
  const [isLinking, setIsLinking] = useState(false)
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false)

  const devices = devicesData?.devices ?? []
  const enabledSerialNumbers = useMemo(
    () => new Set(enabledDevicesData?.devices?.map(device => device.sn) ?? []),
    [enabledDevicesData?.devices],
  )
  const selectedCount = selectedSerialNumbers.length

  useEffect(() => {
    if (hasInitializedSelection || isLoadingEnabledDevices || !devices.length) {
      return
    }

    const initialSelectedSerialNumbers = devices
      .filter(device => enabledSerialNumbers.has(device.sn))
      .map(device => device.sn)

    setSelectedSerialNumbers(initialSelectedSerialNumbers)
    setHasInitializedSelection(true)
  }, [devices, enabledSerialNumbers, hasInitializedSelection, isLoadingEnabledDevices])

  const toggleDevice = (sn: string) => {
    setSelectedSerialNumbers(prevSelected => {
      if (prevSelected.includes(sn)) {
        return prevSelected.filter(item => item !== sn)
      }

      return [...prevSelected, sn]
    })
  }

  const requestOAuthRedirectLocation = async (params: OAuthLoginParams | null) => {
    if (!params) {
      throw new Error("OAuth login params are missing.")
    }

    const { client_id, redirect_uri, response_type, state, code_challenge, code_challenge_method, scope } = params

    const responseBody = await requestOAuthCode({
      clientId: client_id,
      redirectUri: redirect_uri,
      responseType: response_type,
      state,
      codeChallenge: code_challenge,
      codeChallengeMethod: code_challenge_method,
      scope,
    }).unwrap()

    const redirectLocation = responseBody.redirectUrl

    if (!redirectLocation) {
      throw new Error("Authorization code redirect did not include redirectUrl.")
    }

    return redirectLocation
  }

  const onLink = async () => {
    if (!token || !oauthLoginParams) {
      return
    }
    if (!selectedCount) {
      setStatusMessage(linkingSelectionMessage)
      return
    }

    try {
      setIsLinking(true)
      const payload = {
        devices: devices.map(device => ({
          sn: device.sn,
          enabled: selectedSerialNumbers.includes(device.sn),
        })),
      }

      await setEnabled(payload).unwrap()

      const redirectLocation = await requestOAuthRedirectLocation(oauthLoginParams)

      try {
        await logoutMobileUser().unwrap()
      } catch {
      } finally {
        clearMobileAuthToken()
      }

      window.location.assign(redirectLocation)
    } catch {
      setStatusMessage(linkingErrorMessage)
      toast.error(linkingErrorMessage)
      setIsLinking(false)
    }
  }

  const deviceCountLabel = `${devices.length} device${devices.length === 1 ? "" : "s"}`
  const linkButtonLabel = `Link ${selectedCount} Thermostat${selectedCount === 1 ? "" : "s"}`

  return (
    <main className={styles.page}>
      <header className={styles.brand}>
        <img className={styles.logo} src={logo} alt="Nuve" />
      </header>

      <section className={styles.panel}>
        <div className={styles.content}>
          <h1 className={styles.title}>Connecting to Amazon Alexa</h1>
          <p className={styles.subtitle}>We found {deviceCountLabel}</p>
          <p className={styles.helperText}>
            Pick the thermostat(s) Alexa can control.
            <br />
            Select at least one device to continue. You can change this anytime.
          </p>

          {(isLoadingDevices || isLoadingEnabledDevices) && <p className={styles.status}>Loading devices...</p>}
          {statusMessage && <p className={styles.status}>{statusMessage}</p>}
          {isDevicesError && (
            <p className={styles.status}>
              Failed to load devices:{" "}
              {String((devicesError as { status?: unknown; data?: unknown })?.status ?? "error")}
            </p>
          )}
          {isEnabledDevicesError && (
            <p className={styles.status}>
              Failed to load enabled devices:{" "}
              {String((enabledDevicesError as { status?: unknown; data?: unknown })?.status ?? "error")}
            </p>
          )}

          <div className={styles.deviceList}>
            {devices.map(device => {
              const checked = selectedSerialNumbers.includes(device.sn)

              return (
                <label key={device.sn} className={styles.deviceRow} data-selected={checked}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDevice(device.sn)}
                  />
                  <span className={styles.checkboxVisual} aria-hidden="true" data-selected={checked} />
                  <div className={styles.deviceCopy}>
                    <div className={styles.deviceName}>{device.friendly_name}</div>
                    <div className={styles.deviceSn}>SN: {device.sn}</div>
                  </div>
                </label>
              )
            })}
          </div>

          <button
            type="button"
            className={styles.linkButton}
            onClick={onLink}
            disabled={!selectedCount || isSaving || isLinking || !token}
          >
            {isLinking || isSaving ? "Linking..." : linkButtonLabel}
          </button>
        </div>
      </section>
    </main>
  )
}
